import { CommonModule } from '@angular/common'
import { Component, output, signal } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormsModule } from '@angular/forms'
import { Subject, debounceTime, switchMap } from 'rxjs'
import { InternationalizationService } from '../../../../../services/internationalization.service'
import { UserSearchResult } from '../../../interfaces/chat.interface'
import { MessageChatStateService } from '../../../services/message-chat-state.service'
import { MessageChatService } from '../../../services/message-chat.service'

@Component({
  selector: 'app-create-group-modal',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './create-group-modal.component.html',
  styleUrl: './create-group-modal.component.scss',
})
export class CreateGroupModalComponent {
  private readonly searchSubject = new Subject<string>()

  readonly isOpen = signal(false)
  readonly groupName = signal('')
  readonly searchText = signal('')
  readonly searchResults = signal<UserSearchResult[]>([])
  readonly selectedMembers = signal<UserSearchResult[]>([])

  readonly create = output<{ name: string; memberIds: number[] }>()

  constructor(
    private chatService: MessageChatService,
    private state: MessageChatStateService,
    protected translateService: InternationalizationService
  ) {
    this.searchSubject
      .pipe(
        debounceTime(300),
        switchMap((term) => this.chatService.findActiveUsers(term)),
        takeUntilDestroyed()
      )
      .subscribe((users) => {
        const currentId = this.state.currentUserId()
        this.searchResults.set(users.filter((u) => u.userId !== currentId))
      })
  }

  open(): void {
    this.isOpen.set(true)
    this.groupName.set('')
    this.searchText.set('')
    this.searchResults.set([])
    this.selectedMembers.set([])
  }

  close(): void {
    this.isOpen.set(false)
  }

  onSearchChange(value: string): void {
    this.searchText.set(value)
    if (value.trim().length >= 2) {
      this.searchSubject.next(value)
    } else {
      this.searchResults.set([])
    }
  }

  selectUser(user: UserSearchResult): void {
    if (!this.selectedMembers().find((m) => m.userId === user.userId)) {
      this.selectedMembers.update((members) => [...members, user])
    }
    this.searchText.set('')
    this.searchResults.set([])
  }

  removeMember(userId: number): void {
    this.selectedMembers.update((members) => members.filter((m) => m.userId !== userId))
  }

  onSubmit(): void {
    const name = this.groupName().trim()
    if (!name || this.selectedMembers().length === 0) {
      return
    }

    this.create.emit({
      name,
      memberIds: this.selectedMembers().map((m) => m.userId),
    })
    this.close()
  }

  onBackdropClick(event: MouseEvent): void {
    if ((event.target as HTMLElement).classList.contains('modal-backdrop')) {
      this.close()
    }
  }
}
